import {
  postGameByGameIdEndMutation,
  postGameStartMutation,
  type PostGameStartResponse,
} from "@aura-frontend/omnibus-az-client";
import { useMutation } from "@tanstack/react-query";
import { toast } from "@win-reality/react-ui";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useParams } from "react-router-dom";

import { Spinner } from "@/components/ui/spinner";
import { getErrorDetail } from "@/utils/errorUtils";

import { useGetActiveGame } from "../../../hooks/useGetActiveGame";
import {
  resetBullpenPitchCount,
  setBullpenActive,
  setBullpenPitcher,
} from "../../../store/slices/bullpen";
import { setSessionType, setSessionUuid } from "../../../store/slices/session";
import {
  setCurrentFilter,
  setTrackingState,
} from "../../../store/slices/socket";
import { setActiveView } from "../../../store/slices/view";
import type { RootState } from "../../../store/store";
import type { IPlayer } from "../../../store/types";
import { mapIPlayerToLineupEntry } from "../../../utils/api-helpers";
import { useUpdateGameState } from "../../../utils/useUpdateGameState";
import { resumeGameEffects } from "../../modals/ActiveGameModal/helpers";
import { SelectPitcherModal } from "../../modals/SelectPitcherModal";
import { BullpenView } from "./BullpenView";

/**
 * BullpenModeView - Main container for Bullpen mode
 *
 * On mount, checks if there's an active bullpen on this machine.
 * - If an active bullpen exists, auto-resumes it (rehydrates Redux state).
 * - If no active bullpen, sets up session defaults.
 *
 * Renders BullpenView once isBullpenActive is true in Redux.
 * Also manages socket event filtering for pitch data.
 */
const BullpenModeView = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { id: machineId } = useParams() as { id: string };
  const { fetchActiveGame } = useGetActiveGame();
  const updateGameState = useUpdateGameState();
  const [isCheckingActiveGame, setIsCheckingActiveGame] = useState(true);
  const [showPitcherSelect, setShowPitcherSelect] = useState(false);

  const isBullpenActive = useSelector(
    (state: RootState) => state.bullpen.isBullpenActive,
  );

  const { mutate: startBullpenMutation, isPending: isStartingBullpen } =
    useMutation({
      mutationFn: postGameStartMutation().mutationFn,
      onError: (error: Error) => {
        const errorMessage = getErrorDetail(error);
        toast(`Error starting bullpen: ${errorMessage}`, {
          variant: "error",
        });
      },
    });

  const handleStartBullpen = async (player: IPlayer, teamUuid: string) => {
    // Defensively end any lingering active game before starting a new one.
    try {
      const activeGame = await fetchActiveGame();
      if (activeGame?.game) {
        const { mutationFn: endGameFn } = postGameByGameIdEndMutation();
        if (endGameFn)
          await endGameFn(
            {
              body: {},
              path: {
                // @ts-expect-error gameUuid is not correctly typed in the API spec.
                game_id: activeGame.game.gameUuid,
              },
            },
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            {} as any,
          );
      }
    } catch {
      // Previous session could not be ended — don't try to start a new one
      toast("Failed to end the previous session. Please try again.", {
        variant: "error",
      });
      return;
    }

    startBullpenMutation(
      {
        body: {
          type: "bullpen",
          machineUuid: machineId,
          awayTeamLineup: [],
          homeTeamId: teamUuid,
          homeTeamLineup: [mapIPlayerToLineupEntry(player, "pitcher")],
        },
      },
      {
        onSuccess: (data: PostGameStartResponse) => {
          if (!data.gameState || !data.game) {
            throw new Error("Missing gameState or game in response");
          }

          dispatch(setBullpenPitcher(player));
          dispatch(resetBullpenPitchCount());
          dispatch(setBullpenActive(true));
          dispatch(setSessionUuid(data.game.gameUuid));
          dispatch(setSessionType("bullpen"));
          dispatch(setTrackingState(true));
          dispatch(setActiveView("Bullpen Mode"));
          setShowPitcherSelect(false);
        },
      },
    );
  };

  // On mount, check for an active bullpen and auto-resume if found
  useEffect(() => {
    // Branch 1: Already active (resume path or localStorage)
    if (isBullpenActive) {
      setIsCheckingActiveGame(false);
      return;
    }

    // Branch 2: Navigated from Welcome — skip API check, show setup
    if ((location.state as { showSetup?: boolean })?.showSetup) {
      dispatch(setSessionType("bullpen"));
      dispatch(setTrackingState(true));
      dispatch(setActiveView("Bullpen Mode"));
      setShowPitcherSelect(true);
      setIsCheckingActiveGame(false);
      // Clear state so page refresh does a full check
      window.history.replaceState({}, "");
      return;
    }

    // Branch 3: Direct navigation/refresh — full API check (existing logic)
    const checkAndResumeBullpen = async () => {
      const activeGamePayload = await fetchActiveGame();

      if (activeGamePayload?.game?.type === "bullpen") {
        resumeGameEffects(dispatch, {
          game: activeGamePayload.game,
          awayTeamPlayers: activeGamePayload.awayTeamPlayers,
          homeTeamPlayers: activeGamePayload.homeTeamPlayers,
        });
        updateGameState(activeGamePayload.gameState, activeGamePayload.game);
      } else {
        // End any non-bullpen active game before showing pitcher select
        if (activeGamePayload?.game) {
          try {
            const { mutationFn: endGameFn } = postGameByGameIdEndMutation();
            if (endGameFn)
              await endGameFn(
                {
                  body: {},
                  path: {
                    // @ts-expect-error gameUuid is not correctly typed in the API spec.
                    game_id: activeGamePayload.game.gameUuid,
                  },
                },
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                {} as any,
              );
          } catch {
            // If ending fails, still proceed to show pitcher select
          }
        }

        dispatch(setSessionType("bullpen"));
        dispatch(setTrackingState(true));
        dispatch(setActiveView("Bullpen Mode"));
        setShowPitcherSelect(true);
      }

      setIsCheckingActiveGame(false);
    };

    checkAndResumeBullpen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Set socket filter to show only pitch data events
  useEffect(() => {
    dispatch(setCurrentFilter("has_pitch_data"));

    return () => {
      dispatch(setCurrentFilter("all"));
    };
  }, [dispatch]);

  if (isCheckingActiveGame) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Spinner className="h-8 w-8" />
        <span className="ml-3 text-base text-muted-foreground">
          Loading bullpen...
        </span>
      </div>
    );
  }

  if (!isBullpenActive) {
    return (
      <SelectPitcherModal
        isOpen={showPitcherSelect}
        onClose={() => setShowPitcherSelect(false)}
        onSubmit={handleStartBullpen}
        isLoading={isStartingBullpen}
        title="Select Bullpen Pitcher"
        submitLabel="Start Bullpen"
      />
    );
  }

  return <BullpenView />;
};

export default BullpenModeView;
